import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { SignUpFormStyles as FileUploadStyles } from "../components/auth/styles/SignUpForm.styles";
import { trackEvent } from "src/Amplitude";
import PageLayoutController from "src/components/core/PageLayoutController";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";
import CandBMonogramNavy from "src/icons/CandBMonogramNavy";
import { FileUpload } from "src/components/application/FileUploadField";
import { resetFiles, useOnboarding } from "src/store/features/onboarding.slice";
import React, { useEffect } from "react";
import api from "src/store/features/api";
import GenericMessageModal from "src/components/wallets/Modal/Withdraw/GenericMessageModal";
import { Link } from "react-router-dom";
import StyledButton from "src/components/dashboard/StyledButton";
import { useAppDispatch } from "src/store/store";

export interface FileUploadValues {
  comment: string;
}

const FileUploadSchema = Yup.object().shape({
  comment: Yup.string()
    .min(3, "Comment must be at least 3 characters")
    .max(200, "Comment must be less than 200 characters"),
});

const initialValues: FileUploadValues = {
  comment: "",
};

const DESKTOP_LAYOUT = [
  ["APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR"],
  ["FORM", "FORM", "FORM", "FORM", "FORM", "FORM"],
  ["BOTTOM_BAR", "BOTTOM_BAR", "BOTTOM_BAR", "BOTTOM_BAR", "BOTTOM_BAR", "BOTTOM_BAR"],
];

const MOBILE_LAYOUT = [
  ["APP_BAR", "APP_BAR"],
  ["FORM", "FORM"],
  ["BOTTOM_BAR", "BOTTOM_BAR"],
];

const SecureFileUpload = () => {
  const classes = FileUploadStyles();
  const dispatch = useAppDispatch();
  const [documents, setDocuments] = React.useState<string[]>([]);
  const code = new URLSearchParams(window.location.search).get("code");
  const [validateToken] = api.useValidateSecureUploadTokenMutation();
  const [open, setOpen] = React.useState(false);
  const [openErrorSnackBar, setOpenErrorSnackBar] = React.useState(false);
  const [message, setMessage] = React.useState("This link is expired, please request a new one");
  const [submitSecureUploadForm, { isLoading }] = api.useHandleSecureFileUploadMutation();
  const [submitted, setSubmitted] = React.useState(false);

  useEffect(() => {
    if (code)
      validateToken({
        decodeMessageDto: {
          message: code,
        },
      })
        .unwrap()
        .then((res: string) => {
          if (res.includes("expired")) {
            setOpenErrorSnackBar(true);
          } else {
            setMessage("Link valid for " + res);
            setOpen(true);
          }
        })
        .catch(() => {
          setOpenErrorSnackBar(true);
        });
  }, [code]);

  const { uploadedFiles } = useOnboarding();

  useEffect(() => {
    const arrayOfDocumentIds = uploadedFiles.map((file) => file.contentVersionId);
    setDocuments(arrayOfDocumentIds);
  }, [uploadedFiles]);

  const handleSubmit = async (userInput: FileUploadValues) => {
    await submitSecureUploadForm({
      secureFileUploadDto: {
        code: code || "",
        documents: documents.map((document) => document.toString()),
        comments: userInput.comment,
      },
    });
    setSubmitted(true);
  };

  const isInvalid = () => {
    return documents.length === 0;
  };

  return (
    <>
      {submitted ? (
        <Grid container justifyContent="center" style={{ height: "500px", zIndex: 10000 }}>
          <GenericMessageModal
            customMessage={`Thank you! Your submission has been successfully received. We’ll be in touch with you shortly to discuss the next steps.`}
            hideButton
            halfSize
          />
          <StyledButton
            onClick={() => {
              dispatch(resetFiles());
              setSubmitted(false);
            }}
            sx={{ maxWidth: { xs: "200px", md: "400px" } }}>
            Upload another file
          </StyledButton>
        </Grid>
      ) : (
        <PageLayoutController
          mobileLayout={MOBILE_LAYOUT}
          desktopLayout={DESKTOP_LAYOUT}
          container={false}
          spacing={10}>
          <ComponentLayoutWrapper
            gridArea="APP_BAR"
            fadeInDirection="none"
            state={{}}
            loadingComponent={<></>}>
            <Grid
              container
              sx={{
                padding: "40px 20px",
                display: "flex",
                alignItems: "flex-start",
              }}>
              <Grid item>
                <CandBMonogramNavy height={"32"} width={"28"} />
              </Grid>
            </Grid>
          </ComponentLayoutWrapper>
          <ComponentLayoutWrapper
            gridArea="FORM"
            fadeInDirection="none"
            state={{}}
            loadingComponent={<></>}>
            <div className={classes.container} style={{ justifyContent: "center" }}>
              <Grid container className={classes.logoBox}>
                Secure File Upload
              </Grid>
              {(openErrorSnackBar || !code) && (
                <Grid item container className={classes.disclaimer}>
                  <Typography sx={{ color: "#002035" }}>This link is expired or invalid</Typography>
                  <Typography className={classes.termsOfUse}>
                    Please reach out to{" "}
                    <a
                      href="mailto:onboarding@calebandbrown.com"
                      className={classes.loginLinkStyle}>
                      Caleb & Brown support
                    </a>{" "}
                    team for a new link.
                  </Typography>
                </Grid>
              )}
              <Formik
                initialValues={initialValues}
                validationSchema={FileUploadSchema}
                onSubmit={handleSubmit}>
                {({ errors, touched, values }) => {
                  return (
                    <Form
                      className={classes.form}
                      style={{
                        display: openErrorSnackBar ? "none" : "flex",
                      }}>
                      <Grid container item xs={12} mb={2}>
                        <FileUpload />
                      </Grid>
                      <Grid item className={classes.textField}>
                        <Field
                          as={TextField}
                          className={classes.root}
                          InputProps={{
                            disableUnderline: true,
                          }}
                          InputLabelProps={{
                            className: classes.label,
                          }}
                          fullWidth
                          name="comment"
                          type="text"
                          label="Comment (Optional)"
                          error={errors.comment && touched.comment}
                          helperText={touched.comment && errors.comment}
                          variant="filled"
                        />
                      </Grid>
                      <Grid item className={classes.textField}>
                        <Typography className={classes.termsOfUse} align="left">
                          Please note that by completing this request and submitting this form, you
                          consent to Caleb & Brown collecting, using, disclosing and otherwise
                          dealing with your personal information, which includes information like
                          your biometric data, as outlined in our{" "}
                          <Link
                            to="https://calebandbrown.com/privacy-policy/"
                            target="_blank"
                            rel="noopener"
                            onClick={() =>
                              trackEvent("User Clicked External Link", {}, "Privacy Policy")
                            }>
                            Privacy Policy.
                          </Link>
                        </Typography>
                      </Grid>
                      {isLoading && <LinearProgress />}
                      <Button
                        disableRipple
                        className={classes.button}
                        style={{ marginBottom: "40px" }}
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isInvalid()}>
                        Submit
                      </Button>
                    </Form>
                  );
                }}
              </Formik>
              {/* SUCCESS NOTICE */}
              <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={open}
                onClose={() => setOpen(false)}>
                <Alert onClose={() => setOpen(false)} severity="info" sx={{ width: "100%" }}>
                  {message}
                </Alert>
              </Snackbar>
            </div>
          </ComponentLayoutWrapper>
        </PageLayoutController>
      )}
    </>
  );
};

export default SecureFileUpload;
